import { createBatchedParamsArrayFromIds, creatorbase } from '@round/api';
import { useReportCampaignsStats } from './hooks/useReportCampaignsStats';
import uniq from 'lodash/uniq';
import { isNumber } from 'utility/utility';
import {
    useBrandsQueriesByIds,
    useCampaigns as useCampaignsQuery,
    useProjectsListQueries,
    useReport,
    useTeams,
} from '@round/creatorbase-helpers/api-hooks';
import { getReportCampaignsListParams } from './hooks/reportCampaignsListParams';
import useReportId from '../hooks/useReportId';

export default function useCampaigns(platform: creatorbase.Platform) {
    const reportId = useReportId();
    const { data: report, isLoading: isReportLoading } = useReport(reportId);
    const teamIds = report?.teams_assigned_to_campaigns ?? [];

    // Resolve row teams from the report's own `teams_assigned_to_campaigns` rather than deriving
    // team ids from the loaded projects. Deriving from projects would chain campaigns → projects →
    // teams, adding another fetch-waterfall layer; the report already carries the full team id set,
    // so teams can load in parallel with projects instead of after them.
    const { data: teamsData, isLoading: isTeamsLoading } = useTeams(
        { id: teamIds.join(','), page_size: teamIds.length },
        {
            enabled: teamIds.length > 0,
            select: (data) => new Map(data.results.map((team) => [team.id, team])),
        }
    );

    const campaignsData = useCampaignsQuery(getReportCampaignsListParams(reportId ?? '', platform), {
        enabled: !!reportId,
    });

    const campaigns = campaignsData.data?.results ?? [];
    const campaignIds = campaigns.map((campaign) => campaign.id);
    const { data: campaignStatsData } = useReportCampaignsStats(campaignIds);

    const projectIds = uniq(campaigns.map((campaign) => campaign.project_id));
    const { projectsData, isProjectsLoading } = useProjectsListQueries(
        createBatchedParamsArrayFromIds(projectIds, (ids) => ({ id: ids.toString() })).map((params) => ({
            params,
            config: { enabled: projectIds.length > 0 },
        })),
        {
            combine: (results) => ({
                projectsData: new Map<number, creatorbase.Project>(
                    results.flatMap((result) => result.data?.results ?? []).map((project) => [project.id, project])
                ),
                isProjectsLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const brandIds = uniq([...projectsData.values()].map((project) => project.brand_id).filter(isNumber));
    const brandsData = useBrandsQueriesByIds([brandIds], {
        combine: (queries) => {
            const query = queries[0];
            const status: 'loading' | 'success' | 'error' =
                !query || query.isPending ? 'loading' : query.isError ? 'error' : 'success';
            const dict: Record<
                number,
                { data: creatorbase.Brand | null; status: typeof status; error: string | null }
            > = {};
            for (const id of brandIds) {
                const brand = (query?.data?.results.find((b) => b.id === id) ?? null) as creatorbase.Brand | null;
                dict[id] = { data: brand, status, error: query?.isError ? 'Could not load brands' : null };
            }
            return dict;
        },
    });

    return {
        campaignsData,
        projectsData,
        brandsData,
        teamsData,
        isTeamsLoading: isReportLoading || isTeamsLoading,
        campaignStatsData,
        isProjectsLoading,
        getIsBrandLoading: (campaignId: number) => {
            const projectId = campaigns.find((campaign) => campaign.id === campaignId)?.project_id;
            const project = typeof projectId === 'number' ? projectsData.get(projectId) ?? null : null;
            return Boolean(project && brandsData[project.brand_id]?.status === 'loading');
        },
        getIsCampaignStatsLoading: (campaignId: number) => campaignStatsData[campaignId]?.isLoading ?? false,
    };
}
